<script lang="ts">
    import { _ } from "svelte-i18n"
    import CloseIcon from "svelte-material-icons/Close.svelte"
    import Autorenew from "svelte-material-icons/Autorenew.svelte"
    import ChevronDownIcon from "svelte-material-icons/ChevronDown.svelte"
    import FilterSlider from "./FilterSlider.svelte"
    import { debounce, formatPrice } from "../../../utils"
    import { tick } from "svelte"
    import { checkoutData, location, login } from "../../../store"
    import FilterSelect from "./FilterSelect.svelte"
    import { currency } from "../../../../../api/hooks/config-client"
    import { ralMap } from "../../../config"

    export let preFilter: any
    export let userFilter: { $and: any[] }
    export let products: Product[]

    let innerWidth: number
    let activefilter = true

    $: if (innerWidth < 768) {
        activefilter = false
    } else {
        activefilter = true
    }

    export let filterWidgets: {
        context: string
    }[]

    let filterValues: {
        [filterKey: string]: {
            min: number
            max: number
            values: number[]
            boundReset?: Function
            boundRangeStart?: number
            boundRangeEnd?: number
            boundValue?: any
        }
    }

    export let filterSelection: {
        [filterKey: string]: {
            rangeStart?: number
            rangeEnd?: number
        }
    } = {}

    const widgetConfigs: {
        [key: string]: {
            field: string
            label: string
            this: any
            unit?: string
            tolerance?: number
            valueFormat?: Function
            multiple?: boolean
        }
    } = {
        material: {
            field: "filter_params.materials",
            label: $_("product-filter.material"),
            this: FilterSelect,
            valueFormat(val: any) {
                switch (val) {
                    case "":
                        return $_("product-filter.all-materials")
                    case 1:
                        return $_("product-filter.plastic")
                    case 2:
                        return $_("product-filter.aluminium")
                    case 3:
                        return $_("product-filter.stainless-steel")
                }
            },
        },
        price: {
            field: "price_net" + ($login ? "_dealer" : "_end_customer"),
            label: $_("product-filter.price"),
            this: FilterSlider,
            unit: currency.sign,
            tolerance: 1,
            valueFormat(price: number) {
                return formatPrice(
                    Math.round(price),
                    $checkoutData.vat
                    // @ts-ignore
                ).replace(/.{3}$/, "")
            },
        },
        punchW: {
            field: "filter_params.punching_width",
            label: $_("product-filter.punch-width"),
            this: FilterSlider,
            unit: "mm",
            tolerance: 1,
        },
        punchH: {
            field: "filter_params.punching_height",
            label: $_("product-filter.punch-height"),
            this: FilterSlider,
            unit: "mm",
            tolerance: 1,
        },
        outerW: {
            field: "filter_params.external_dimension_width",
            label: $_("product-filter.outer-width"),
            this: FilterSlider,
            unit: "mm",
            tolerance: 1,
        },
        outerH: {
            field: "filter_params.external_dimension_height",
            label: $_("product-filter.outer-height"),
            this: FilterSlider,
            unit: "mm",
            tolerance: 1,
        },
        size: {
            field: "filter_params.size",
            label: $_("product-filter.size"),
            this: FilterSelect,
            multiple: true,
        },
        color: {
            field: "filter_params.color_code",
            label: $_("product-filter.color"),
            this: FilterSelect,
            multiple: true,
            valueFormat(val: any) {
                let colorBubbleStyle =
                    "display: inline-block; padding-left: 8px; height:15px; border-radius: 10px; margin-right: 10px; vertical-align: middle;"
                const ral = ralMap[val.toString().padStart(4, "0")]
                let color = ral?.HEX
                let colorName = ral?.de
                switch (val) {
                    case 108:
                        console.log(val)
                        color = "transparent"
                        colorBubbleStyle += "background-image: url(/media/filter/color-108.png); background-size: 100%;"
                        colorName = "verzinkt"
                        break
                    case 109:
                        color = "transparent"
                        colorBubbleStyle += "background-image: url(/media/filter/color-109.png); background-size: 100%;"
                        colorName = "Edelstahl"
                        break
                }
                if (color || colorName) {
                    return (
                        '<span style="' +
                        colorBubbleStyle +
                        " background-color: " +
                        color +
                        '; border: 1px solid black;">&nbsp;</span> ' +
                        (colorName || val)
                    )
                }
                return val
            },
        },
        /* TODO:
        year: {
            field: "filter_params.year_of_construction_from",
            label: $_("product-filter.construction-year"),
            this: FilterSlider,
            unit: "ab",
            tolerance: 1,
        },
        */
    }

    const widgetFieldMapping = {}
    Object.keys(widgetConfigs).forEach((k) => {
        widgetFieldMapping[widgetConfigs[k].field] = k
    })

    const load = async () => {
        const _filterValues = {}
        filterValues = undefined
        await tick()

        let _filterWidgetTypes = filterWidgets?.map((w) => w.context) ?? []
        const insertValue = (k, value) => {
            const wKey = widgetFieldMapping[k]
            const curVal = _filterValues[wKey]
            const newObj = {
                min: curVal?.min,
                max: curVal?.max,
                values: curVal?.values ?? [],
            }
            if (_filterWidgetTypes.includes(wKey)) {
                let values: number[] = Array.isArray(value) ? value : [value]

                values.forEach((v) => {
                    if (typeof newObj.min == "undefined" || newObj.min > v) newObj.min = v
                    if (typeof newObj.max == "undefined" || newObj.max < v) newObj.max = v
                    if (!newObj.values.includes(v)) newObj.values.push(v)
                })

                _filterValues[wKey] = newObj
            }
        }

        products.forEach((p) => {
            insertValue("price_net", $login ? p.price_net_dealer : p.price_net_end_customer)
            if (p.filter_params) {
                Object.keys(p.filter_params).forEach((k) => {
                    insertValue("filter_params." + k, p.filter_params[k])
                })
            }
        })

        filterValues = _filterValues
    }

    $: if (filterWidgets && preFilter && products?.length) {
        load()
    }

    let filterChips: {
        label: string
        reset: Function
    }[] = []

    const buildFilter = debounce(() => {
        filterChips = []
        const _filterSelection = {}

        const andFilter = []
        filterWidgets.forEach((w) => {
            const wv = filterValues[w.context]
            const wc = widgetConfigs[w.context]
            if (wv?.boundRangeStart || wv?.boundRangeEnd || wv?.boundValue) {
                filterChips.push({
                    label: wc?.label,
                    reset: wv.boundReset,
                })

                const _f = {}

                const _fs: any = {}
                _filterSelection[widgetFieldMapping[wc.field]] = _fs
                if (wv.boundValue) {
                    if (wc.multiple) {
                        _f[wc.field] = { $in: wv.boundValue }
                    } else {
                        _f[wc.field] = wv.boundValue
                    }
                    _fs.value = wv.boundValue
                } else {
                    const _ff = {}
                    _f[wc.field] = _ff
                    if (wv.boundRangeStart) {
                        _ff["$gte"] = wv.boundRangeStart
                        _fs.rangeStart = wv.boundRangeStart
                    }
                    if (wv.boundRangeEnd) {
                        _ff["$lte"] = wv.boundRangeEnd
                        _fs.rangeEnd = wv.boundRangeEnd
                    }
                }

                andFilter.push(_f)
            }
        })

        if (andFilter.length) {
            userFilter = { $and: andFilter }
            filterSelection = _filterSelection
        } else {
            userFilter = undefined
            filterSelection = undefined
        }
    }, 500)

    $: if (filterValues) {
        buildFilter()
    }
</script>

<svelte:window bind:innerWidth />

{#key $location.path}
    {#if filterWidgets?.length && filterValues}
        <div class="firstheadline">
            <div class="inner">
                Filter
                <!-- svelte-ignore a11y-click-events-have-key-events -->
                <span class="icon" class:active="{activefilter}" on:click="{() => (activefilter = !activefilter)}"
                    ><ChevronDownIcon size="24px" /></span
                >
            </div>
        </div>
        <div id="product_filter" class:visible="{activefilter}">
            {#each filterWidgets as widget}
                {#if widget.context && widgetConfigs[widget.context]}
                    {#if filterValues[widget.context] && filterValues[widget.context].values?.length > 1}
                        <div>
                            <svelte:component
                                this="{widgetConfigs[widget.context].this}"
                                bind:reset="{filterValues[widget.context].boundReset}"
                                bind:rangeStart="{filterValues[widget.context].boundRangeStart}"
                                bind:rangeEnd="{filterValues[widget.context].boundRangeEnd}"
                                bind:value="{filterValues[widget.context].boundValue}"
                                {...widgetConfigs[widget.context]}
                                min="{filterValues[widget.context].min}"
                                max="{filterValues[widget.context].max}"
                                values="{filterValues[widget.context].values}"
                                init="{filterSelection && filterSelection[widget.context]}"
                            />
                        </div>
                    {/if}
                {/if}
            {/each}
        </div>
    {/if}
    <div id="products_top">
        {#if filterChips?.length}
            <div class="tags">
                <li class="resetall">
                    <span class="text">{$_("product-filter.reset-all")}</span>
                    <button on:click="{() => filterChips.forEach((c) => c.reset())}"><Autorenew size="18px" /></button>
                </li>
                {#each filterChips as chip}
                    <li>
                        <span class="text">{chip.label}</span>
                        <button on:click="{() => chip.reset()}"><CloseIcon size="18px" /></button>
                    </li>
                {/each}
            </div>
        {/if}
    </div>
{/key}

<style lang="less">
    @import "../../../css/variables.less";

    .firstheadline {
        height: 60px;
        border-bottom: 1px dashed var(--border-color-2);
        .flex();
        justify-content: flex-start;
        flex-direction: row;
        padding-bottom: 10px;
        margin-bottom: 20px;
        font-size: 1.2rem;
        .inner {
            width: 100%;
            .flex();
            align-items: center;
            justify-content: space-between;
            .icon {
                display: none;
                line-height: 0;
                .transition();
                &.active {
                    transform: rotate(180deg);
                }
                @media (max-width: 768px) {
                    display: inline-block;
                }
            }
        }
    }
    #product_filter {
        .flex();
        flex-wrap: wrap;
        margin-bottom: 50px;
        margin-left: -20px;
        margin-right: -20px;
        .opacity(1);
        visibility: visible;
        .transition();
        @media (max-width: 768px) {
            .opacity(0);
            visibility: hidden;
            max-height: 0;
            margin: 0px;
            &.visible {
                .opacity(1);
                visibility: visible;
                max-height: 800px;
                margin-top: 10px;
                margin-bottom: 50px;
            }
        }
        @media (max-width: 420px) {
            flex-direction: column;
            display: block;
        }
        > div {
            width: 20%;
            padding: 20px;
            @media (max-width: 850px) {
                margin-top: -30px;
            }
            @media (max-width: 1300px) {
                width: 33.33333%;
            }
            @media (max-width: 600px) {
                width: 50% !important;
            }
            @media (max-width: 600px) {
                width: 100% !important;
                margin-bottom: 10px;
                display: block;
            }
        }
        /*.markenauswahl {
            .content {
                .flex();
                margin: -1%;
                li {
                    list-style-type: none;
                    border: solid 1px #999;
                    padding: 10px 20px;
                    margin: 1%;
                    cursor: pointer;
                    line-height: 0;
                    img {
                        width: 100%;
                    }
                }
            }
        }
        .farbauswahl {
            flex-grow: 1;
            width: 50%;
            .content {
                .flex();
                flex-wrap: wrap;
                li {
                    width: 30px;
                    height: 30px;
                    margin: 0 10px 10px 0;
                    box-shadow: inset 0 4px 4px 0 rgba(0, 0, 0, 0.25);
                    border-radius: 100%;
                    cursor: pointer;
                    list-style-type: none;
                    position: relative;
                    &:after {
                        content: "";
                        display: block;
                        width: 11px;
                        height: 4px;
                        border-bottom: 2px solid #fff;
                        border-left: 2px solid #fff;
                        transform: rotate(-45deg) scale(0);
                        transition: transform ease 0.25s;
                        position: absolute;
                        top: 10px;
                        left: 9px;
                    }
                    &.active:after {
                        transform: rotate(-45deg) scale(1);
                    }
                }
            }
        }*/
    }

    #products_top {
        padding-bottom: 10px;
        border-bottom: solid 1px #999;
        .flex();
        justify-content: space-between;
        align-items: center;
        @media (max-width: 850px) {
            flex-direction: column;
            align-items: flex-start;
        }
        .tags {
            .flex();
            flex-wrap: wrap;
            @media (max-width: 850px) {
                margin-bottom: 15px;
            }
            li {
                .flex();
                justify-content: space-between;
                align-items: center;
                list-style-type: none;
                border-radius: 30px;
                background: var(--surface-4);
                color: var(--on-surface-4);
                margin-right: 10px;
                margin-bottom: 5px;
                padding: 6px 15px;
                line-height: 1;
                font-size: 0.8rem;
                .text {
                    margin-right: 6px;
                }
                &.resetall {
                    background: none;
                    border: 1px solid var(--border-color-default);
                }

                button {
                    border: none;
                    outline: none;
                    cursor: pointer;
                    background: none;

                    &:hover {
                        color: var(--primary);
                    }
                    &:active {
                        border: none;
                        outline: none;
                        background: none;
                    }
                }
            }
        }
    }
</style>
